// Panel inline para subir productos nuevos. Tiene DOS estados en una sola
// pantalla (réplica del "agregar producto" del estudio clásico):
//   • Sin fotos (estado explicativo): nombre opcional + card "Imágenes" con 2
//     fotos de ejemplo (según rubro) + tips de cómo subir + botón
//     "Subir imagen del producto" (abre el file picker).
//   • Con fotos: grilla de previews (+ "Agregar"), nombre y "Confirmar".
//
// El upload real (Supabase + naming IA) corre afuera; este panel solo expone
// callbacks. Dos variants visuales:
//   • desktop → header / scroll / footer sticky (px-6 interno). Vive como vista
//     de un side panel.
//   • mobile  → flex-col simple; el host le da padding externo.
//
// Consumers: PresetDetailSheet (Choose → Upload flow) y
// MultiSlotProductPickerSheet.

import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronLeft, Layers, Plus, Upload, X } from 'lucide-react';

export interface UploadProductPanelProps {
  variant: 'mobile' | 'desktop';
  files: File[];
  name: string;
  onNameChange: (v: string) => void;
  onAddMore: (files: FileList | File[]) => void;
  onRemoveFile: (idx: number) => void;
  onCancel: () => void;
  onConfirm: () => void;
  uploading: boolean;
  error: string | null;
  /** Rubro del usuario — define las fotos de ejemplo del estado vacío
   *  (Moda → bolso, Hogar → sillón, resto → botella) y si se muestra el tip
   *  de "producto siendo utilizado". */
  rubro?: string | null;
  /** Si se provee, el estado vacío muestra además el botón "Subir varios
   *  productos". Ya NO abre el file picker directo: abre el popup de alta en
   *  bloque de Mi Marca (nombres editables, Tiendanube, tope por plan), que
   *  monta el host. Antes era exclusivo de Catálogo; ahora va en todos los
   *  modos, con el mismo popup que el resto de las pantallas (ADR-684). */
  onBulkUpload?: () => void;
  /** Oculta el link "Volver" interno (cuando el host ya da un back, p.ej.
   *  la top bar mobile en la pantalla full-screen). */
  hideBack?: boolean;
}

// Fotos de ejemplo por rubro (mismas que el "agregar producto" clásico).
function getExampleImages(rubro?: string | null) {
  if (rubro === 'Moda e Indumentaria') {
    return {
      img1: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/bolso.jpeg',
      img2: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/bolso_siendo_usado.png',
    };
  }
  if (rubro === 'Hogar y Decoración') {
    return {
      img1: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/sillon.png',
      img2: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/sillon_casa.png',
    };
  }
  return {
    img1: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/botella.png',
    img2: 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/botella_uso.png',
  };
}

export const UploadProductPanel: React.FC<UploadProductPanelProps> = ({
  variant,
  files,
  name,
  onNameChange,
  onAddMore,
  onRemoveFile,
  onCancel,
  onConfirm,
  uploading,
  error,
  rubro,
  onBulkUpload,
  hideBack = false,
}) => {
  const addMoreRef = useRef<HTMLInputElement | null>(null);
  const hasFiles = files.length > 0;
  const isFashion = rubro === 'Moda e Indumentaria';
  const examples = getExampleImages(rubro);
  // En moda el tile "+" sugiere qué otra foto sumar (detalle / espalda de la
  // prenda); en el resto de los rubros alcanza con "Agregar".
  const addTileLabel = isFashion ? 'Agregar detalle o parte de atrás' : 'Agregar';

  // Previews: para cada File, generamos una URL local (createObjectURL)
  // y la liberamos al desmontar/cambiar files para no leakear memoria.
  const [previews, setPreviews] = useState<string[]>([]);
  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [files]);

  const isDesktop = variant === 'desktop';

  // Input file compartido (lo dispara tanto "Subir imagen del producto" del
  // estado vacío como el tile "+" del estado con fotos).
  const fileInput = (
    <input
      ref={addMoreRef}
      type="file"
      accept="image/*"
      multiple
      className="hidden"
      onChange={(e) => {
        if (e.target.files && e.target.files.length > 0) {
          onAddMore(e.target.files);
          e.target.value = '';
        }
      }}
    />
  );

  const nameInput = (
    <div>
      <label className="block text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-1.5">
        Nombre (opcional)
      </label>
      <input
        type="text"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        placeholder="Déjalo vacío para que lo complete la IA"
        disabled={uploading}
        className="w-full h-11 px-4 rounded-xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary disabled:opacity-50 transition-colors"
      />
    </div>
  );

  // ── Estado vacío: explicación de cómo subir (ejemplos + tips). Sin
  // contenedor con borde: las imágenes y la aclaración van sueltas. ──────
  const imagesExplainer = (
    <div>
      <p className="block text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-1.5">
        Imágenes
      </p>
      <div className="flex items-center gap-3">
        {[examples.img1, examples.img2].map((src, i) => (
          <div
            key={i}
            className="flex-1 aspect-square rounded-xl bg-neutral-50 overflow-hidden relative"
          >
            <img
              src={src}
              alt="Ejemplo de foto de producto"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <span className="absolute bottom-1.5 right-1.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-success text-white shadow-sm">
              <Check className="w-3.5 h-3.5" strokeWidth={3} />
            </span>
          </div>
        ))}
      </div>
      <ul className="mt-3 text-[13px] text-[#3C3C43]/65 list-disc pl-4 space-y-1 leading-snug">
        <li>Subí una foto en donde se vea claramente el producto que querés vender.</li>
        {isFashion && (
          <li>Fotos del producto siendo utilizado (sin mostrar el rostro) funcionan mejor.</li>
        )}
      </ul>
    </div>
  );

  // Botones del estado vacío (van al footer, bottom-aligned).
  const uploadButtons = (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => addMoreRef.current?.click()}
        disabled={uploading}
        className="w-full inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 text-white text-[15.5px] font-semibold shadow-sm transition-colors"
      >
        <Upload className="w-5 h-5" />
        Subir imagen del producto
      </button>
      {/* Subida en bloque — abre el popup de Mi Marca (mismo que el resto de
          las pantallas de producto). Cada imagen = un producto distinto. */}
      {onBulkUpload && (
        <button
          type="button"
          onClick={onBulkUpload}
          disabled={uploading}
          title="Subí varias imágenes a la vez; cada una será un producto distinto"
          className="w-full inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/85 disabled:opacity-40 text-[15.5px] font-semibold transition-colors"
        >
          <Layers className="w-5 h-5" />
          Subir varios productos
        </button>
      )}
    </div>
  );

  // ── Estado con fotos: grilla de previews + tile "Agregar" ──────────────
  const grid = (
    <div>
      <p className="block text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-1.5">
        Imágenes
      </p>
      <div className="grid grid-cols-2 gap-2.5">
        {files.map((file, idx) => (
          <div
            key={`${file.name}-${idx}`}
            className="relative rounded-xl overflow-hidden bg-neutral-100 aspect-square ring-1 ring-black/[0.05]"
          >
            {previews[idx] && (
              <img
                src={previews[idx]}
                alt={file.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <button
              type="button"
              onClick={() => onRemoveFile(idx)}
              disabled={uploading}
              aria-label="Quitar imagen"
              className="absolute top-1.5 right-1.5 w-7 h-7 inline-flex items-center justify-center rounded-full bg-black/55 backdrop-blur-sm text-white hover:bg-black/70 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {/* Tile "+" para agregar más imágenes (mismo producto, otro ángulo) */}
        <button
          type="button"
          onClick={() => addMoreRef.current?.click()}
          disabled={uploading}
          className="aspect-square rounded-xl border-2 border-dashed border-neutral-300 hover:border-brand-primary/40 bg-white disabled:opacity-40 disabled:cursor-not-allowed flex flex-col items-center justify-center gap-1.5 text-[#3C3C43]/55 hover:text-brand-primary transition-colors"
        >
          <Plus className="w-5 h-5" strokeWidth={2.25} />
          <span className="text-[12px] font-medium leading-tight text-center px-2">
            {addTileLabel}
          </span>
        </button>
      </div>
    </div>
  );

  const errorBlock = error ? (
    <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-[13px] text-red-700">
      {error}
    </div>
  ) : null;

  const confirmButton = (
    <button
      type="button"
      onClick={onConfirm}
      disabled={uploading || files.length === 0}
      className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[14.5px] font-semibold shadow-sm transition-colors"
    >
      {uploading ? 'Subiendo…' : 'Confirmar'}
    </button>
  );

  const headerSubtitle = hasFiles
    ? 'Sumá fotos del mismo producto desde distintos ángulos para que Atlas lo recree mejor. El nombre es opcional.'
    : 'Subí una foto de tu producto para empezar.';

  // ── Desktop: header / scroll / footer fijos ─────────────────────────
  if (isDesktop) {
    return (
      <div className="flex flex-col h-full">
        {fileInput}
        <div className="shrink-0 px-6 pt-6 pb-3">
          {!hideBack && (
            <button
              type="button"
              onClick={onCancel}
              disabled={uploading}
              className="inline-flex items-center gap-1 text-brand-primary text-[13px] mb-3 hover:opacity-75 disabled:opacity-40 transition-opacity"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Volver
            </button>
          )}
          <p className="text-[16px] font-semibold text-brand-black/90">
            Nuevo producto
          </p>
          <p className="text-[12.5px] text-[#3C3C43]/55 mt-0.5">
            {headerSubtitle}
          </p>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 space-y-4 pb-3">
          {nameInput}
          {hasFiles ? grid : imagesExplainer}
          {errorBlock}
        </div>
        <div className="shrink-0 px-6 pt-3 pb-6 border-t border-black/[0.05] bg-white">
          {hasFiles ? confirmButton : uploadButtons}
        </div>
      </div>
    );
  }

  // ── Mobile: inline con padding del wrapper externo (el host define la
  // altura; no forzamos h-full para no colapsar en contenedores auto). ──
  return (
    <div className="flex flex-col gap-4">
      {fileInput}
      {!hideBack && (
        <button
          type="button"
          onClick={onCancel}
          disabled={uploading}
          className="inline-flex items-center gap-1 text-brand-primary text-[13px] hover:opacity-75 disabled:opacity-40 transition-opacity self-start"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Volver
        </button>
      )}
      <div>
        <p className="text-[16px] font-semibold text-brand-black/90">
          Nuevo producto
        </p>
        <p className="text-[12.5px] text-[#3C3C43]/55 mt-0.5">{headerSubtitle}</p>
      </div>
      {nameInput}
      {hasFiles ? grid : imagesExplainer}
      {errorBlock}
      {hasFiles ? confirmButton : uploadButtons}
    </div>
  );
};
